<script setup lang="ts">
import { nextTick, ref, useId, watch } from 'vue'

import BaseCard from '@tix/ds/components/atoms/BaseCard.vue'
import FormInput from '@tix/ds/components/atoms/FormInput.vue'
import ActionCard from '@tix/ds/components/molecules/ActionCard.vue'

export type DonationPreset = {
  id: string
  /** Display label, pre-formatted for the active currency (e.g. "$50", "£100"). */
  label: string
}

export type DonationCustomAmount = {
  id: string
  /** ISO 4217 currency code used to render the input's currency symbol. */
  currency: string
  min?: number
  max?: number
}

export type DonationAmountSelection = {
  ticketTypeId: string
  faceValue?: number
}

type Props = {
  heading?: string
  /** Render order matches array order; the widget does not sort. */
  presetAmounts: DonationPreset[]
  /** Pay-what-you-want field. Pass `null` to hide it. */
  customAmount?: DonationCustomAmount | null
  /**
   * The authoritative selection (cart-derived). The widget echoes local intent
   * optimistically on interaction, but reconciles to this whenever it changes —
   * e.g. after a server round-trip corrects or drops the cart. `null` = nothing
   * selected.
   */
  selection?: DonationAmountSelection | null
  /** Freeze presets + custom amount (e.g. while payment submit is in flight). */
  disabled?: boolean
  /** Red card border after Pay with no reserved amount. */
  invalid?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  heading: 'How would you like to donate?',
  customAmount: null,
  selection: null,
  disabled: false,
  invalid: false,
})

const emit = defineEmits<{
  selectPreset: [id: string]
  /** `amount: null` means the field is empty. */
  'update:customAmount': [amount: number | null]
}>()

const headingId = useId()
const selected = ref<string | null>(props.selection?.ticketTypeId ?? null)
const customAmountValue = ref<number | null>(
  props.selection?.ticketTypeId === props.customAmount?.id ? (props.selection?.faceValue ?? null) : null,
)
const customAmountInput = ref<{ validateAndReturnError: () => string | boolean | null } | null>(null)

// Reconcile local UI to the authoritative selection whenever it changes (e.g. a
// server round-trip corrected or dropped the cart). Only refill the custom field
// when the cart actually holds a custom amount — an empty/invalid custom entry
// clears the cart (selection === null) but the typed value should stay put.
watch(
  () => props.selection,
  (selection) => {
    selected.value = selection?.ticketTypeId ?? null
    if (selection && props.customAmount && selection.ticketTypeId === props.customAmount.id) {
      customAmountValue.value = selection.faceValue ?? null
    }
  },
)

const handlePresetClick = (preset: DonationPreset) => {
  if (props.disabled) return

  selected.value = preset.id
  customAmountValue.value = null
  nextTick(() => {
    // Clear any validation errors from the previous custom amount.
    customAmountInput.value?.validateAndReturnError()
  })
  emit('selectPreset', preset.id)
}

const handleCustomAmountUpdate = (value: string) => {
  if (props.disabled || !props.customAmount) return

  selected.value = props.customAmount.id
  // Empty field emits ''; Number('') is 0, so treat empty as null explicitly.
  const numeric = value === '' ? null : Number(value)
  customAmountValue.value = Number.isFinite(numeric) ? numeric : null
  customAmountInput.value?.validateAndReturnError()
  // TODO Consider debouncing this to avoid excessive cart requests.
  emit('update:customAmount', customAmountValue.value)
}
</script>

<template>
  <BaseCard align="center" class="donation-widget" :class="{ 'is-invalid': invalid }" padding="comfy">
    <template #header>
      <h2 :id="headingId" class="h3">{{ heading }}</h2>
    </template>

    <div class="body">
      <div v-if="presetAmounts.length" :aria-labelledby="headingId" class="presets" role="radiogroup">
        <ActionCard
          v-for="preset in presetAmounts"
          :key="preset.id"
          :active="selected === preset.id"
          :aria-checked="selected === preset.id"
          class="button"
          :disabled="disabled"
          padding="compact"
          role="radio"
          @click="handlePresetClick(preset)"
        >
          {{ preset.label }}
        </ActionCard>
      </div>

      <FormInput
        v-if="customAmount"
        :id="customAmount.id"
        ref="customAmountInput"
        :currency="customAmount.currency"
        :disabled="disabled"
        :hideLabel="true"
        :hideRequiredLabel="true"
        label="Other amount"
        :max="customAmount.max"
        :min="customAmount.min"
        :modelValue="customAmountValue ?? undefined"
        placeholder="Other amount"
        :showOptionalLabel="false"
        step="0.01"
        type="money"
        @update:modelValue="handleCustomAmountUpdate"
      />
    </div>
  </BaseCard>
</template>

<style scoped>
.donation-widget {
  &.is-invalid {
    border-color: var(--error-border-color);
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-1-5);
  }

  .presets {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-0-75);

    .button {
      text-align: center;
    }
  }
}
</style>
